import React, { useState } from 'react';
import { Box, Button, Group, NumberInput, Select, Text, TextInput } from '@mantine/core';
import { IconCheck, IconTrash } from '@tabler/icons-react';
import { PLANNING_RESOURCE_TYPES, type PlanningResourceType } from '../../../api/planningResourceApis';
import { RateLabel } from './RateLabel';

export type BulkApplyPatch = Partial<{
  type: PlanningResourceType | string;
  unitOfMeasure: string;
  maxUnits: number;
  maxUnitsPerDay: number;
  standardRate: number;
  overtimeRate: number;
  costPerUse: number;
  initials: string;
  email: string;
  calendarId: string;
  notes: string;
}>;

type BulkFormState = {
  type: string | null;
  unitOfMeasure: string;
  maxUnits: number | '';
  maxUnitsPerDay: number | '';
  standardRate: number | '';
  overtimeRate: number | '';
  costPerUse: number | '';
  initials: string;
  email: string;
  calendarId: string | null;
  notes: string;
};

const EMPTY_BULK_FORM: BulkFormState = {
  type: null,
  unitOfMeasure: '',
  maxUnits: '',
  maxUnitsPerDay: '',
  standardRate: '',
  overtimeRate: '',
  costPerUse: '',
  initials: '',
  email: '',
  calendarId: null,
  notes: '',
};

const TYPE_OPTIONS = PLANNING_RESOURCE_TYPES.map((value) => ({
  value,
  label: value.charAt(0) + value.slice(1).toLowerCase(),
}));

export type ResourceBulkToolbarProps = {
  selectedCount: number;
  calendarOptions: Array<{ value: string; label: string }>;
  disabled?: boolean;
  onClearSelection: () => void;
  onApply: (patch: BulkApplyPatch) => void;
  onDelete: () => void;
};

/**
 * Owns its own form state so typing here does not re-render the resource table.
 */
export function ResourceBulkToolbar({
  selectedCount,
  calendarOptions,
  disabled = false,
  onClearSelection,
  onApply,
  onDelete,
}: ResourceBulkToolbarProps) {
  const [form, setForm] = useState<BulkFormState>(EMPTY_BULK_FORM);

  const handleApply = () => {
    const patch: BulkApplyPatch = {};
    if (form.type) patch.type = form.type;
    if (form.unitOfMeasure.trim()) patch.unitOfMeasure = form.unitOfMeasure.trim();
    if (form.maxUnits !== '') patch.maxUnits = form.maxUnits;
    if (form.maxUnitsPerDay !== '') patch.maxUnitsPerDay = form.maxUnitsPerDay;
    if (form.standardRate !== '') patch.standardRate = form.standardRate;
    if (form.overtimeRate !== '') patch.overtimeRate = form.overtimeRate;
    if (form.costPerUse !== '') patch.costPerUse = form.costPerUse;
    if (form.initials.trim()) patch.initials = form.initials.trim();
    if (form.email.trim()) patch.email = form.email.trim();
    if (form.calendarId) patch.calendarId = form.calendarId;
    if (form.notes.trim()) patch.notes = form.notes.trim();
    onApply(patch);
  };

  return (
    <Box className="rm-bulk-bar">
      <Group justify="space-between" align="center" wrap="wrap" gap="sm" mb={10}>
        <Group gap="xs">
          <span className="rm-chip rm-chip--selected">{selectedCount} selected</span>
          <Text size="xs" c="dimmed">
            Fill fields below, then apply to all selected rows
          </Text>
        </Group>
        <Button variant="subtle" size="compact-xs" color="gray" onClick={onClearSelection}>
          Clear selection
        </Button>
      </Group>
      <Box className="rm-bulk-fields">
        <Select
          className="rm-bulk-field rm-bulk-field--type"
          size="xs"
          label="Type"
          placeholder="Skip"
          clearable
          data={TYPE_OPTIONS}
          value={form.type}
          onChange={(value) => setForm((prev) => ({ ...prev, type: value }))}
        />
        <TextInput
          className="rm-bulk-field rm-bulk-field--unit"
          size="xs"
          label="Unit"
          placeholder="Skip"
          value={form.unitOfMeasure}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, unitOfMeasure: e.currentTarget.value }))
          }
        />
        <NumberInput
          className="rm-bulk-field rm-bulk-field--num"
          size="xs"
          label="Max units"
          placeholder="Skip"
          hideControls
          decimalScale={2}
          value={form.maxUnits}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              maxUnits: typeof value === 'number' ? value : '',
            }))
          }
        />
        <NumberInput
          className="rm-bulk-field rm-bulk-field--num"
          size="xs"
          label="Max / day"
          placeholder="Skip"
          hideControls
          decimalScale={2}
          value={form.maxUnitsPerDay}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              maxUnitsPerDay: typeof value === 'number' ? value : '',
            }))
          }
        />
        <NumberInput
          className="rm-bulk-field rm-bulk-field--num"
          size="xs"
          label={<RateLabel>Std rate</RateLabel>}
          placeholder="Skip"
          hideControls
          decimalScale={2}
          value={form.standardRate}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              standardRate: typeof value === 'number' ? value : '',
            }))
          }
        />
        <NumberInput
          className="rm-bulk-field rm-bulk-field--num"
          size="xs"
          label={<RateLabel>OT rate</RateLabel>}
          placeholder="Skip"
          hideControls
          decimalScale={2}
          value={form.overtimeRate}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              overtimeRate: typeof value === 'number' ? value : '',
            }))
          }
        />
        <NumberInput
          className="rm-bulk-field rm-bulk-field--num"
          size="xs"
          label={<RateLabel>Cost / use</RateLabel>}
          placeholder="Skip"
          hideControls
          decimalScale={2}
          value={form.costPerUse}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              costPerUse: typeof value === 'number' ? value : '',
            }))
          }
        />
        <TextInput
          className="rm-bulk-field rm-bulk-field--initials"
          size="xs"
          label="Initials"
          placeholder="Skip"
          value={form.initials}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, initials: e.currentTarget.value }))
          }
        />
        <Select
          className="rm-bulk-field rm-bulk-field--calendar rm-form-select-clearable"
          size="xs"
          label="Calendar"
          placeholder="Skip"
          clearable
          searchable
          data={calendarOptions}
          value={form.calendarId}
          onChange={(value) => setForm((prev) => ({ ...prev, calendarId: value }))}
        />
        <TextInput
          className="rm-bulk-field rm-bulk-field--notes"
          size="xs"
          label="Notes"
          placeholder="Skip"
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.currentTarget.value }))}
        />
        <TextInput
          className="rm-bulk-field rm-bulk-field--email"
          size="xs"
          label="Email"
          placeholder="Skip"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.currentTarget.value }))}
        />
        <Box className="rm-bulk-actions">
          <Button
            size="xs"
            radius="md"
            color="brand"
            leftSection={<IconCheck size={14} />}
            onClick={handleApply}
            disabled={disabled}
          >
            Apply
          </Button>
          <Button
            size="xs"
            radius="md"
            color="red"
            variant="light"
            leftSection={<IconTrash size={14} />}
            onClick={onDelete}
            disabled={disabled}
          >
            Delete selected resources
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
